import { useState, useRef } from 'react'
import { Camera, Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { uploadAvatar } from '@/lib/api'

interface AvatarUploadProps {
    userId: string
    currentUrl: string | null
    fullName: string | null
    onUploadComplete: (url: string) => void
}

export function AvatarUpload({ userId, currentUrl, fullName, onUploadComplete }: AvatarUploadProps) {
    const [uploading, setUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const initials = fullName
        ? fullName
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
        : 'U'

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        // Basic validation
        if (!file.type.startsWith('image/')) {
            toast.error('Bitte lade nur Bilder hoch.')
            return
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error('Das Bild darf maximal 2MB groß sein.')
            return
        }

        try {
            setUploading(true)
            const publicUrl = await uploadAvatar(file, userId)
            onUploadComplete(publicUrl)
            toast.success('Profilbild erfolgreich hochgeladen.')
        } catch (error) {
            console.error('Upload failed:', error)
            toast.error('Upload fehlgeschlagen. Bitte versuche es erneut.')
        } finally {
            setUploading(false)
        }
    }

    const triggerUpload = () => {
        fileInputRef.current?.click()
    }

    return (
        <div className="flex flex-col items-center gap-4">
            <div className="relative group cursor-pointer" onClick={triggerUpload}>
                <Avatar className="h-24 w-24 border-2 border-primary/20 transition-all group-hover:border-primary">
                    <AvatarImage src={currentUrl || undefined} alt={fullName || 'User'} className="object-cover" />
                    <AvatarFallback className="text-xl bg-primary/10 text-primary">{initials}</AvatarFallback>
                </Avatar>

                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                    {uploading ? (
                        <Loader2 className="h-8 w-8 animate-spin text-white" />
                    ) : (
                        <Camera className="h-8 w-8 text-white" />
                    )}
                </div>
            </div>

            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploading}
            />

            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={triggerUpload}
                disabled={uploading}
                className="gap-2"
            >
                {uploading ? (
                    <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Lädt hoch...
                    </>
                ) : (
                    <>
                        <Upload className="h-4 w-4" />
                        Foto ändern
                    </>
                )}
            </Button>
        </div>
    )
}
